import { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader } from 'reactstrap';
import { Post, Discard } from 'Constant';
import { Btn, H5, H6, Spinner } from 'AbstractElements';
import QuestionsForm from './QuestionsForm';
import MiniBarQuestion from './MiniBarQuestion';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { submitQuestion, fetchQuestion } from 'Features/Learning/BankQuestions/Utils/QuestionsUtils';
import { setField, resetState } from 'Features/Learning/BankQuestions/addQuestionSlice';
import { handleEditQuestion, isQuestionEditLocked } from './QuestionsUtils';
import Swal from 'sweetalert2';

const QuestionPostContain = () => {
  const dispatch = useDispatch();
  const history = useNavigate();
  const data = useSelector(state => state.addQuestion)
  const startUpload = useSelector(state => state.addQuestion.isLoading);
  const isUpdate = useSelector(state => state.addQuestion.isUpdate);
  const isEditLocked = isQuestionEditLocked(data);
  // const dataQuestionUpdate = useSelector(state => state.Question.data.data);
  const isLoading = useSelector(state => state.Question.isLoading);
  const filters = useSelector(state => state.Question.filters);
  // const [emptyChoice, setEmptyChoice] = useState(false);
  // const [emptyAnswer, setEmptyAnswer] = useState(false);
  const { page, index } = useParams();
  const isEditRoute = Boolean(page);
  const checkParams = new URLSearchParams(window.location.search).get('params');
  const decodedParams = checkParams ? atob(checkParams) : '';
  const title = checkParams ? decodedParams.split("&")[0].split("=")[1] : '';
  const type = checkParams ? decodedParams.split("&")[1].split("=")[1] : '';
  const sort = checkParams ? decodedParams.split("&")[2].split("=")[1] : '';
  const [isSaveChoice, setIsSaveChoice] = useState(false);
  const [isQuestionHydrated, setIsQuestionHydrated] = useState(!isEditRoute);

  useEffect(() => {
    console.log('dataIsChoiceUpdate', data.isChoiceUpdate);
    if (data.isChoiceUpdate) {
      setIsSaveChoice(true);
    }
  }, [data.isChoiceUpdate])

  useEffect(() => {
    if (!isEditRoute) {
      setIsQuestionHydrated(true);
      return;
    }
    // Sidebar pre-hydrates via onClick before navigation; skip spinner if already loaded.
    if (data.id && data.isUpdate) {
      setIsQuestionHydrated(true);
      return;
    }
    setIsQuestionHydrated(false);
  }, [page, index, isEditRoute, data.id, data.isUpdate]);

  useEffect(() => {
    if (!page || data.id) return;

    let cancelled = false;

    const hydrateQuestionFromRoute = async () => {
      const newFilters = {
        ...filters,
        'filter[title]': title,
        'filter[type]': type,
        'sort': sort,
        'page': page,
      };

      try {
        const response = await dispatch(fetchQuestion(newFilters)).unwrap();
        if (cancelled) return;

        const question = response?.data?.[index];
        if (question) {
          handleEditQuestion(dispatch, question, setField);
        }
        setIsQuestionHydrated(true);
      } catch (error) {
        if (!cancelled) {
          console.error('Error during fetchQuestion:', error);
          setIsQuestionHydrated(true);
        }
      }
    };

    hydrateQuestionFromRoute();

    return () => {
      cancelled = true;
    };
  }, [page, data.id, dispatch, filters, index, sort, title, type]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSuccess = async () => {
    if (!page) {
      dispatch(resetState());
      history(`${process.env.PUBLIC_URL}/learning/soal-list`);
      return;
    }

    const newFilters = {
      ...filters,
      'filter[title]': title,
      'filter[type]': type,
      'sort': sort,
      'page': page,
    };

    setIsQuestionHydrated(false);

    try {
      const response = await dispatch(fetchQuestion(newFilters)).unwrap();
      const question = response?.data?.[index];
      if (question) {
        handleEditQuestion(dispatch, question, setField);
      }
    } catch (error) {
      console.error('Error during fetchQuestion:', error);
    } finally {
      setIsQuestionHydrated(true);
    }
  };

  const formRef = useRef();

  const handleSaveChoice = (e) => {
    e.preventDefault();
    if (isEditLocked) return;
    if (formRef.current) {
      formRef.current.triggerSetReduxData(); // Memanggil function di Form
    }
    setIsSaveChoice(false);
  }

  // Helper: shuffle untuk sequential submit (choice dibentuk dari answer)
  // NOTE: keep local (scope kecil) agar tidak mengubah flow komponen lain.
  const shuffleArrayEnsureDifferent = (arr, maxAttempts = 5) => {
    const base = Array.isArray(arr) ? [...arr] : [];
    if (base.length <= 1) return base;

    const isSame = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const next = [...base];
      for (let i = next.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = next[i];
        next[i] = next[j];
        next[j] = tmp;
      }
      if (!isSame(next, base)) return next;
    }

    return base;
  };

  const validateQuestionData = () => {
    const isEmptyChoice = data.choice.some((element) => element === "" || element === undefined);
    const isEmptyAnswer = data.answer.some((element) => element === "" || element === undefined);
    const isInteractiveType = data.type === 'sentence_arrangement' || data.type === 'fill_in_the_blanks';

    // NOTE: data.is_interactive di project ini kadang boolean, kadang number (1/0)
    const isInteractiveEnabled = data.is_interactive === true || data.is_interactive === 1;

    // ✅ Sequential: choice tidak perlu divalidasi (dibentuk dari answer saat submit)
    const isSequentialInteractiveType =
      data.type === 'sentence_arrangement' &&
      isInteractiveEnabled &&
      data.interactive_type === 'sequential';

    // Untuk tipe non fill_in_the_blanks biasanya butuh choices,
    // tapi khusus sequential: choice akan digenerate, jadi tidak wajib diisi user.
    const requiresChoices = data.type !== 'fill_in_the_blanks' && !isSequentialInteractiveType;

    // Chart interactive types (yang kamu sebut: pie, bar, line)
    const isChartInteractiveType =
      data.type === 'sentence_arrangement' &&
      isInteractiveEnabled &&
      ['pie_chart', 'bar_chart', 'line_chart'].includes(data.interactive_type);

    // Bar/Line chart (untuk validasi step)
    const isBarLineChartInteractiveType =
      data.type === 'sentence_arrangement' &&
      isInteractiveEnabled &&
      ['bar_chart', 'line_chart'].includes(data.interactive_type);

    // Helper: cek numeric-only (integer). Disallow selain angka (no letters, no symbols).
    const isDigitsOnly = (val) => {
      const s = String(val ?? '').trim();
      return /^\d+$/.test(s);
    };

    // Step handling (string dari input number tetap bisa kebaca sebagai string di state)
    const stepRaw = String(data.step ?? '').trim();
    const stepIsValidDigits = isDigitsOnly(stepRaw);
    const stepInt = stepIsValidDigits ? parseInt(stepRaw, 10) : NaN;

    // Helper: total untuk pie chart (anggap integer)
    const pieTotal = data.answer.reduce((sum, v) => sum + Number(String(v ?? '').trim()), 0);

    // Helper: dragpin active?
    // NOTE: Drag Pins sekarang dianggap milik tipe fill_in_the_blanks (bukan sentence_arrangement)
    const isDragPinsInteractiveType =
      data.type === 'fill_in_the_blanks' &&
      isInteractiveEnabled &&
      data.interactive_type === 'dragpin';

    // Helper: clickable active?
    // NOTE: Clickable sekarang dianggap milik tipe fill_in_the_blanks
    const isClickableInteractiveType =
      data.type === 'fill_in_the_blanks' &&
      isInteractiveEnabled &&
      data.interactive_type === 'clickable';

    // Helper: validasi room_count harus integer 1..6
    const dragPinsRoomsRaw = data?.room_count;
    const dragPinsRoomsInt = Number(dragPinsRoomsRaw);
    const isValidDragPinsRooms =
      Number.isInteger(dragPinsRoomsInt) && dragPinsRoomsInt >= 1 && dragPinsRoomsInt <= 6;

    // ✅ Helper: validasi pin_count harus integer 1..10 (wajib dipilih saat dragpin)
    const dragPinsPinsRaw = data?.pin_count;
    const dragPinsPinsInt = Number(dragPinsPinsRaw);
    const isValidDragPinsPins =
      Number.isInteger(dragPinsPinsInt) && dragPinsPinsInt >= 1 && dragPinsPinsInt <= 10;

    // ✅ Helper: validasi label tiap pin (wajib untuk dragpin)
    const dragpinPinsMeta = Array.isArray(data?.interactive_detail?.dragpin_pins)
      ? data.interactive_detail.dragpin_pins
      : [];

    const isDragpinPinsLabelMissing =
      isDragPinsInteractiveType &&
      isValidDragPinsPins &&
      Array.from({ length: dragPinsPinsInt }).some((_, idx) => {
        const label = dragpinPinsMeta?.[idx]?.label;
        return String(label ?? '').trim() === '';
      });

    const validations = [
      { condition: data.title === '', message: 'Label soal belum diisi.' },
      { condition: data.description === '', message: 'Soal belum diisi.' },
      { condition: data.type === '', message: 'Tipe soal belum dipilih.' },

      // ✅ Skip validasi choice untuk sequential
      { condition: requiresChoices && data.choice.length === 0, message: 'Pilihan Jawaban belum diisi.' },
      { condition: requiresChoices && isEmptyChoice, message: 'Ada Pilihan Jawaban belum diisi.' },

      { condition: data.answer.length === 0, message: 'Jawaban belum diisi.' },
      { condition: isEmptyAnswer, message: 'Ada Jawaban yang belum diisi.' },

      // Min 2 items untuk tipe interaktif Pie/Bar/Line Chart
      // (cek length array karena yang diminta: "answernya minimal 2")
      {
        condition: isChartInteractiveType && data.answer.length < 2,
        message: 'Untuk tipe interaktif Pie/Bar/Line Chart, jumlah jawaban minimal 2 item.',
      },

      // Validasi Step untuk Bar/Line Chart
      // Step wajib angka > 0
      {
        condition: isBarLineChartInteractiveType && (!stepRaw || !stepIsValidDigits || stepInt <= 0),
        message: 'Untuk Bar/Line Chart, Step (Kelipatan) wajib diisi dengan angka lebih dari 0.',
      },

      // Step harus "habis membagi" setiap answer => setiap answer harus kelipatan dari step
      {
        condition:
          isBarLineChartInteractiveType &&
          stepRaw &&
          stepIsValidDigits &&
          stepInt > 0 &&
          data.answer.some((answer) => {
            // Guard: kalau bukan angka, akan ditangani oleh validasi numeric-only di bawah
            if (!isDigitsOnly(answer)) return false;
            const ansInt = parseInt(String(answer).trim(), 10);
            return ansInt % stepInt !== 0;
          }),
        message: 'Untuk Bar/Line Chart, Step harus habis membagi setiap nilai jawaban.',
      },

      { condition: data.answer_explanation === '', message: 'Pembahasan Jawaban belum diisi.' },
      { condition: isInteractiveType && isInteractiveEnabled && !data.interactive_type, message: 'Interaktif tipe belum dipilih.' },

      // Untuk pie/bar/line: answer harus angka saja
      {
        condition: isChartInteractiveType && data.answer.some((answer) => answer !== '' && !isDigitsOnly(answer)),
        message: 'Untuk tipe interaktif Pie/Bar/Line Chart, nilai jawaban hanya boleh berisi angka.',
      },

      // pie minimal 5
      {
        condition:
          data.interactive_type === 'pie_chart' &&
          isInteractiveEnabled &&
          data.answer.some((answer) => answer !== '' && isDigitsOnly(answer) && parseInt(String(answer).trim(), 10) < 5),
        message: 'Untuk Pie Chart, nilai minimal harus 5.',
      },

      // Untuk pie_chart: total answer harus 100
      {
        condition:
          data.interactive_type === 'pie_chart' &&
          isInteractiveEnabled &&
          // only check total if all answers are digits (biar error message-nya gak tabrakan)
          data.answer.every((answer) => answer !== '' && isDigitsOnly(answer)) &&
          pieTotal !== 100,
        message: 'Untuk Pie Chart, total nilai jawaban harus 100.',
      },

      // Wajib pilih jumlah room ketika interactive_type = dragpin
      {
        condition: isDragPinsInteractiveType && !isValidDragPinsRooms,
        message: 'Untuk tipe interaktif Drag Pins, jumlah room wajib dipilih (1 sampai 6).',
      },

      // ✅ Wajib pilih jumlah pins ketika interactive_type = dragpin
      {
        condition: isDragPinsInteractiveType && !isValidDragPinsPins,
        message: 'Untuk tipe interaktif Drag Pins, jumlah pins wajib dipilih (1 sampai 10).',
      },

      // ✅ Wajib isi label setiap pin untuk dragpin (dipakai di table)
      {
        condition: isDragpinPinsLabelMissing,
        message: 'Untuk tipe interaktif Drag Pins, label tiap pins wajib diisi.',
      },

      { condition: data.answer.length < 4 && data.interactive_type === 'sequential', message: 'Jawaban harus lebih dari 4 untuk tipe interaktif urutan.' },

      // Clickable: dibatasi hanya ketika fill_in_the_blanks + interactive enabled
      {
        condition:
          isClickableInteractiveType &&
          data.answer.some((answer) => answer !== '' && !['0', '1', '2'].includes(answer)),
        message: 'Untuk tipe interaktif clickable, jawaban hanya boleh 0, 1, atau 2.',
      },
    ];

    const error = validations.find(v => v.condition);
    return error || null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // ✅ Sequential: bentuk choice dari answer saat submit supaya lolos validasi & payload konsisten
    const isInteractiveEnabled = data.is_interactive === true || data.is_interactive === 1;
    const isSequentialInteractiveType =
      data.type === 'sentence_arrangement' &&
      isInteractiveEnabled &&
      data.interactive_type === 'sequential';

    const dataForSubmit = isSequentialInteractiveType
      ? {
        ...data,
        // Choice diacak dari answer (answer = kunci)
        choice: shuffleArrayEnsureDifferent(Array.isArray(data.answer) ? data.answer : []),
      }
      : data;

    const error = validateQuestionData();
    if (error) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error.message,
      });
    } else {
      dispatch(submitQuestion({ ...dataForSubmit, successCallback: handleSuccess }));
    }
  };

  const handleDiscard = (e) => {
    e.preventDefault();
    dispatch(resetState());
    history(`${process.env.PUBLIC_URL}/learning/soal-list`);
  };

  return (
    <Container fluid={true}>
      {startUpload && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            <Card>
              <CardBody>
                <div className="loader-box">
                  <Spinner attrSpinner={{ className: 'loader-30' }} />
                </div>
                <H5 attrH5={{ className: 'sub-title mb-0 text-center' }}>{'Sedang proses unggah...'}</H5>
              </CardBody>
            </Card>
          </div>
        </div>
      )}
      <Row>
        <Col sm={page ? "9" : "12"}>
          <Card>
            <CardHeader className='pb-0'><H5>{isUpdate ? '' : 'Soal Baru'}</H5></CardHeader>
            {(isLoading || (isEditRoute && !isQuestionHydrated)) ? (
              <Col lg="12">
                <div className="loader-box">
                  <Spinner attrSpinner={{ className: 'loader-30' }} />
                </div>
                <H6 attrH6={{ className: 'sub-title mb-0 text-center' }}>{'Sedang mengambil data...'}</H6>
              </Col>
            ) : (
              <CardBody className="add-post">
                {isEditLocked && (
                  <div className="alert alert-warning" role="alert">
                    Soal ini sudah dikerjakan siswa. Kunci jawaban (benar/salah, penempatan, dll.) tetap terkunci; pertanyaan, teks pilihan ganda, deskripsi pin, pembahasan, video, dan audio masih bisa diubah.
                  </div>
                )}
                <QuestionsForm
                  ref={formRef}
                  key={data.id || `edit-${page}-${index}`}
                  isContentLocked={isEditLocked}
                />
                <div className="btn-showcase text-end">
                  {isSaveChoice && !isEditLocked && (
                    <Btn attrBtn={{ color: 'warning', type: 'button', onClick: handleSaveChoice }}>Save Choice</Btn>
                  )}
                  <Btn attrBtn={{ color: 'primary', type: 'submit', onClick: handleSubmit }}>{Post}</Btn>
                  <Btn attrBtn={{ color: 'light', type: 'reset', onClick: handleDiscard }}>{Discard}</Btn>
                </div>
              </CardBody>
            )}
          </Card>
        </Col>
        {page && (
          <Col sm="3">
            <MiniBarQuestion />
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default QuestionPostContain;